import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Footer } from '@/components/Footer';
import { 
  ArrowLeft,
  MapPin,
  Star,
  Phone,
  Mail,
  Search,
  Leaf,
  Calendar,
  MessageCircle,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';


export const PanchkarmaCenters = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const consultationId = location.state?.consultationId;
  
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    try {
      const response = await api.get(`/api/panchkarma-centers`);
      setCenters(response.data);
    } catch (error) {
      console.error('Error fetching centers:', error);
      toast.error('Failed to load centers');
    } finally {
      setLoading(false);
    }
  };

  const filteredCenters = centers.filter(center =>
    center.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    center.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
    center.treatments.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]" data-testid="panchkarma-centers-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-stone-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Panchkarma Centers
            </h1>
            <p className="text-sm text-stone-500">Find certified detox treatment centers</p>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative h-64 overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1711714956204-e1d84e4d8879"
          alt="Spa interior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(40,23%,96%)] to-transparent"></div>
        <div className="absolute bottom-8 left-0 right-0 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Authentic Panchkarma Treatments
            </h2>
            <p className="text-stone-600 max-w-2xl">
              Book your detoxification journey at certified Ayurvedic centers with experienced practitioners
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            type="text"
            placeholder="Search by name, location, or treatment..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Centers List */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 pb-12">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="card-elevated p-6 animate-pulse">
                <div className="h-48 bg-stone-200 rounded-xl mb-4"></div>
                <div className="h-6 bg-stone-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-stone-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredCenters.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Leaf className="w-10 h-10 text-stone-400" />
            </div>
            <h3 className="text-xl font-semibold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              No Centers Found
            </h3>
            <p className="text-stone-600">
              {searchQuery ? 'Try adjusting your search' : 'Centers will be added soon'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCenters.map(center => (
              <div 
                key={center.center_id} 
                className="center-card"
                data-testid={`center-card-${center.center_id}`}
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={center.image_url || 'https://images.unsplash.com/photo-1711714956204-e1d84e4d8879'}
                    alt={center.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {center.name}
                    </h3>
                    {center.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-[hsl(43,70%,52%)] text-[hsl(43,70%,52%)]" />
                        <span className="text-sm font-medium">{center.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 text-stone-500 text-sm mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{center.location}</span>
                  </div>
                  
                  <p className="text-stone-600 text-sm mb-4 line-clamp-2">
                    {center.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {center.treatments.slice(0, 3).map((treatment, index) => (
                      <span key={index} className="px-2 py-1 bg-[hsl(135,15%,33%)/0.1] text-[hsl(135,15%,33%)] rounded-full text-xs">
                        {treatment}
                      </span>
                    ))}
                    {center.treatments.length > 3 && (
                      <span className="px-2 py-1 bg-stone-100 text-stone-500 rounded-full text-xs">
                        +{center.treatments.length - 3} more
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                    <span className="text-[hsl(22,70%,44%)] font-semibold">{center.price_range}</span>
                    <Button
                      onClick={() => navigate(`/booking/${consultationId || 'new'}/${center.center_id}`)}
                      className="btn-primary text-sm"
                      data-testid={`book-btn-${center.center_id}`}
                    >
                      Book Now
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Discovery Call CTA */}
        <div className="mt-12 bg-gradient-to-br from-[hsl(22,70%,44%)] to-[hsl(35,80%,45%)] rounded-2xl p-8 text-white" data-testid="discovery-call-cta">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Can't Find a Center Near You?
            </h2>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">
              No worries! Book a free discovery call with us and we'll help you find the perfect Panchkarma center 
              in your location or discuss how we can organize your wellness journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://tidycal.com/aiays/client-enquiry"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-[hsl(22,70%,44%)] px-6 py-3 rounded-full font-semibold hover:bg-white/90 transition-colors"
                data-testid="discovery-call-btn"
              >
                <Calendar className="w-5 h-5" />
                Book a Discovery Call
              </a>
              <a
                href="mailto:me@ayurvedaconsult.online"
                className="inline-flex items-center justify-center gap-2 bg-white/20 text-white px-6 py-3 rounded-full font-semibold hover:bg-white/30 transition-colors"
              >
                <Mail className="w-5 h-5" />
                Send Enquiry
              </a>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};
